import { Cog6ToothIcon } from "@heroicons/react/16/solid"
import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { USER_KEYS } from "~/api/cache"
import { userService } from "~/api/services/user.service"
import LogoutIcon from "~/components/Icons/Logout"

import { STEP_LINK_BASE, STEP_LINK_INACTIVE } from "./proposal-setup-rail"

/** The permissions V1's Setup menu shows its Admin link for. */
export const ADMIN_LINK_PERMISSIONS = [
  `vision_admin`,
  `vision_api_key_admin`,
  `vision_proposal_admin`,
  `vision_user_admin`,
  `vision_financing_admin`,
  `vision_equipment_admin`,
] as const

const RAIL_DIVIDER = `mx-auto my-5 h-px w-[95%] bg-bp-border-strong`

/**
 * The account links at the foot of the Battery and Solar Setup rails, as V1's
 * Setup menu has: Admin for an actor with an admin permission, then Logout for
 * everyone. Logout clears a session left from another account, which Vision
 * would otherwise deny the proposal to. A rail without its own divider asks
 * for one.
 *
 * The proposal trees mount outside App, so useAuth may not have read the
 * profile: the rail reads it itself, sharing useAuth's cache entry.
 */
export const ProposalSetupAccountLinks = ({
  divider = false,
}: {
  divider?: boolean
}): JSX.Element => {
  const { t } = useTranslation()
  const { data: profile } = useQuery({
    queryKey: USER_KEYS.profile(),
    queryFn: () => userService.getProfile(),
    retry: false,
    staleTime: 1000 * 60 * 60 * 5,
  })
  const isAdmin = ADMIN_LINK_PERMISSIONS.some(
    (permission) => profile?.permissions.includes(permission) ?? false,
  )

  return (
    <>
      {divider ? <div className={RAIL_DIVIDER} /> : null}
      {isAdmin ? (
        <Link to="/admin" className={`${STEP_LINK_BASE} ${STEP_LINK_INACTIVE}`}>
          <Cog6ToothIcon className="h-4 w-4" />
          {t(`admin`)}
        </Link>
      ) : null}
      <Link to="/logout" className={`${STEP_LINK_BASE} ${STEP_LINK_INACTIVE}`}>
        <LogoutIcon className="h-4 w-4" />
        {t(`logout`)}
      </Link>
    </>
  )
}
